/**
 * Human-readable guidance for Ready to Send review (W15-E02-006 / W15-E02-010).
 */

const REVIEW_REASON_GUIDANCE = Object.freeze({
  CATALOGUE_MATCH_UNCERTAIN: {
    title: "Catalogue match uncertain",
    detail:
      "One or more inventory lines could not be matched to a unique catalogue SKU. Volumes may use defaults until fixed.",
    action:
      "Edit lead inventory to catalogue-friendly names, then Re-run pricing — or wait for n8n and Adopt if its report is better.",
  },
  MISSING_SCALE_REFERENCE: {
    title: "Missing scale reference",
    detail: "No reliable size/dimension reference for an item.",
    action: "Add size hints on the lead or wait for n8n enrichment.",
  },
  AMBIGUOUS_ITEM: {
    title: "Ambiguous item",
    detail: "Item description matches multiple catalogue entries.",
    action: "Clarify the item name or quantity on the lead, then Re-run pricing.",
  },
  UNREADABLE_MEDIA: {
    title: "Unreadable media",
    detail: "Photos or media could not be interpreted for volume.",
    action: "Re-upload clearer photos on the lead or correct inventory manually.",
  },
  PACKING_PRODUCT_UNPRICED: {
    title: "Packing product unpriced",
    detail: "A packing add-on has no configured price.",
    action: "Fix pricing settings or remove the packing line, then Re-run pricing.",
  },
  MISSING_MONETARY_SETTING: {
    title: "Missing pricing setting",
    detail: "A required monetary system setting is missing.",
    action: "Configure the setting in System Settings, then Re-run pricing.",
  },
  CORRIDOR_RATE_MISSING: {
    title: "Corridor rate missing",
    detail:
      "This Shared Load route has no live inter-province corridor rate. The suggested total cannot be sent until the corridor is configured.",
    action:
      "Click Add corridor on this panel, enter rate/m³ (and optional minimum), save, then Calculate suggested price.",
  },
  EXCLUSIVE_LONG_DISTANCE_POLICY_PENDING: {
    title: "Exclusive pricing pending",
    detail:
      "Interprovince exclusive-load pricing is not live for this quote. Shared Load corridor rates do not apply.",
    action:
      "Enter a customer price manually, or enable exclusive catalogue pricing and add an exclusive rate — do not add a Shared Load corridor rate.",
  },
  EXCLUSIVE_RATE_MISSING: {
    title: "Exclusive rate missing",
    detail:
      "Exclusive catalogue pricing is enabled but no matching exclusive rate-card row was found.",
    action:
      "Add an exclusive catalogue row (region × truck) in Rate Cards, then Re-run pricing — Shared Load corridor rates do not apply.",
  },
  W15_E02_005_NOT_READY: {
    title: "Preparation not ready",
    detail: "Review reasons or PDFs must be resolved before approval.",
    action: "Clear review reasons above and confirm both PDFs are ready.",
  },
});

const BLOCKER_GUIDANCE = Object.freeze({
  W15_E02_005_NOT_READY: {
    title: "Preparation not ready",
    detail:
      "Inventory review must be cleared and both PDFs must be ready before you can approve.",
  },
  ADMIN_APPROVAL_REQUIRED: {
    title: "Ops approval required",
    detail:
      "Pricing looks ready — click Approve for send once all other blockers are cleared.",
  },
  AUTOMATED_PREPARATION_NOT_FOUND: {
    title: "No automated preparation",
    detail: "This quote has no automated preparation snapshot.",
  },
});

/**
 * @param {string} code
 * @returns {{ title: string, detail: string, action?: string }}
 */
export function describeReviewReason(code) {
  const key = String(code || "").trim();
  if (REVIEW_REASON_GUIDANCE[key]) {
    return REVIEW_REASON_GUIDANCE[key];
  }
  return {
    title: key.replace(/_/g, " ").toLowerCase(),
    detail: key,
  };
}

/**
 * @param {string} code
 * @returns {{ title: string, detail: string }}
 */
export function describeBlocker(code) {
  const key = String(code || "").trim();
  if (BLOCKER_GUIDANCE[key]) {
    return BLOCKER_GUIDANCE[key];
  }
  return {
    title: key.replace(/_/g, " ").toLowerCase(),
    detail: key,
  };
}

/**
 * @param {{ reviewReasons?: object[], blockers?: string[], preparationReady?: boolean, pdfs?: object }} review
 * @returns {{ headline: string, steps: string[] }}
 */
export function buildReviewNextSteps(review) {
  const steps = [];
  const reasons = review?.reviewReasons || [];
  const blockers = (review?.blockers || []).filter(
    (code) => code !== "ADMIN_APPROVAL_REQUIRED"
  );
  const pdfs = review?.pdfs || {};

  if (reasons.length > 0) {
    const uniqueActions = [
      ...new Set(
        reasons
          .map((reason) => describeReviewReason(reason?.code).action)
          .filter(Boolean)
      ),
    ];
    if (uniqueActions.length > 0) {
      steps.push(...uniqueActions);
    } else {
      steps.push("Resolve the review reasons listed above, then Re-run pricing.");
    }
  }

  if (!pdfs.quotePdf?.ready || !pdfs.quoteItemsListPdf?.ready) {
    steps.push(
      "Wait for Quote PDF and Items PDF to reach ready (or Re-run pricing to regenerate)."
    );
  }

  if (blockers.length === 0 && review?.preparationReady === false) {
    steps.push("Clear remaining blockers, then Approve for send.");
  }

  if (
    review?.preparationReady === true &&
    (review?.blockers || []).includes("ADMIN_APPROVAL_REQUIRED")
  ) {
    steps.push("Click Approve for send when you are satisfied with pricing.");
  }

  const headline =
    reasons.length > 0
      ? "Fix inventory or enrichment issues before approving"
      : blockers.length > 0
        ? "Complete preparation checks before approving"
        : "Review pricing, then approve for send";

  return {
    headline,
    steps: [...new Set(steps)],
  };
}

/**
 * @param {object|null|undefined} match
 * @returns {string}
 */
export function formatCatalogueMatchStatusLabel(match) {
  const status = match?.matchStatus || "none";
  if (status === "unique") return "Matched";
  if (status === "ambiguous") return "Multiple matches";
  return "No exact match";
}

/**
 * @param {object|null|undefined} suggestion
 * @returns {string}
 */
export function formatCatalogueSuggestionLabel(suggestion) {
  if (!suggestion) return "";
  const label =
    suggestion.suggestedLeadLabel ||
    suggestion.displayName ||
    "Unknown SKU";
  const parts = [`Use “${label}”`];
  if (suggestion.sectionLabel) {
    parts.push(suggestion.sectionLabel);
  }
  if (suggestion.opsM3 != null) {
    parts.push(`${Number(suggestion.opsM3).toFixed(2)} ops m³`);
  }
  if (suggestion.billableM3 != null) {
    parts.push(`${Number(suggestion.billableM3).toFixed(2)} billable m³`);
  }
  return parts.join(" · ");
}

/**
 * @param {"catalogue"|"n8n"|string} source
 * @returns {string}
 */
export function formatEnrichmentSourceLabel(source) {
  if (source === "catalogue") {
    return "Offline catalogue";
  }
  if (source === "n8n") {
    return "n8n AI";
  }
  if (source === "ai") {
    return "OS AI enrichment";
  }
  return String(source || "unknown");
}

/**
 * Enrichment report slots shown in the pricing panel (catalogue + secondary).
 * @param {object|null|undefined} enrichmentReports
 * @param {"ai"|"n8n"|null|undefined} [expectedSecondary]
 * @returns {Array<"catalogue"|"ai"|"n8n">}
 */
export function listEnrichmentReportSources(
  enrichmentReports,
  expectedSecondary = null
) {
  const reports = enrichmentReports || {};
  if (reports.ai) return ["catalogue", "ai"];
  if (reports.n8n) return ["catalogue", "n8n"];
  if (expectedSecondary === "ai" || expectedSecondary === "n8n") {
    return ["catalogue", expectedSecondary];
  }
  // Prefer OS AI while waiting when ENV hint is absent (ADR-006 cutover).
  return ["catalogue", "ai"];
}

/**
 * Secondary slot used for wait copy / relaunch (never invents n8n when AI is expected).
 * @param {object|null|undefined} review
 * @returns {"ai"|"n8n"|null}
 */
export function resolveSecondaryEnrichmentSource(review) {
  const reports = review?.enrichmentReports || {};
  if (reports.ai) return "ai";
  if (reports.n8n) return "n8n";
  const expected = review?.expectedSecondaryEnrichmentSource;
  if (expected === "ai" || expected === "n8n") return expected;
  const statusSource = review?.secondaryEnrichmentStatus?.source;
  if (statusSource === "ai" || statusSource === "n8n") return statusSource;
  return "ai";
}

/**
 * @param {object|null|undefined} review
 * @returns {boolean}
 */
export function shouldOfferRelaunchEstimation(review) {
  if (!review || review.canReprice === false) return false;
  const secondary = resolveSecondaryEnrichmentSource(review);
  if (secondary !== "ai" && secondary !== "n8n") return false;
  const reports = review.enrichmentReports || {};
  if (reports[secondary]) return false;
  return true;
}

/**
 * @param {object|null|undefined} review
 * @returns {string}
 */
export function describeSecondaryEnrichmentWait(review) {
  const reports = review?.enrichmentReports || {};
  if (reports.ai || reports.n8n) return "";
  const secondary = resolveSecondaryEnrichmentSource(review);
  const status = review?.secondaryEnrichmentStatus;
  if (
    status?.status === "failed" &&
    (!status.source || status.source === secondary)
  ) {
    const label =
      secondary === "n8n" ? "n8n AI" : "OS AI enrichment";
    return `${label} failed${
      status.error ? ` (${status.error})` : ""
    } — use Relaunch estimation to try again. Catalogue volumes remain available.`;
  }
  if (secondary === "n8n") {
    return "n8n enrichment has not completed yet — suggested volumes may use catalogue defaults only.";
  }
  return "OS AI enrichment has not completed yet — suggested volumes may use catalogue defaults only.";
}

/**
 * Captions under Adopt buttons — only for slots shown in the enrichment panel.
 * @param {object|null|undefined} review
 * @returns {Array<"catalogue"|"ai"|"n8n">}
 */
export function listAdoptGuidanceSources(review) {
  return listEnrichmentReportSources(
    review?.enrichmentReports,
    resolveSecondaryEnrichmentSource(review)
  );
}

/**
 * Sum catalogue / enrichment item billable m³ when present.
 * @param {object[]} items
 * @returns {number|null}
 */
function sumEnrichmentItemsBillableM3(items) {
  let sum = 0;
  let hasAny = false;
  for (const item of items) {
    if (item?.billableM3 != null && Number.isFinite(Number(item.billableM3))) {
      sum += Number(item.billableM3);
      hasAny = true;
    }
  }
  return hasAny ? sum : null;
}

/**
 * @param {object|null|undefined} report
 * @returns {object|null}
 */
export function summarizeEnrichmentReport(report) {
  if (!report || typeof report !== "object") {
    return null;
  }
  const items = Array.isArray(report.items) ? report.items : [];
  const itemReviewCount = items.reduce((count, item) => {
    const reasons = Array.isArray(item?.reviewReasons) ? item.reviewReasons : [];
    return count + reasons.length;
  }, 0);

  const aggregateBillable =
    report.aggregate?.billableM3 != null &&
    Number.isFinite(Number(report.aggregate.billableM3))
      ? Number(report.aggregate.billableM3)
      : null;

  return {
    expectedM3: report.aggregate?.expectedM3 ?? null,
    billableM3: aggregateBillable ?? sumEnrichmentItemsBillableM3(items),
    confidence: report.aggregate?.confidence ?? null,
    itemCount: items.length,
    topLevelReviewCount: Array.isArray(report.reviewReasons)
      ? report.reviewReasons.length
      : 0,
    itemReviewCount,
    completedAt: report.completedAt ?? null,
    provider: report.provider ?? null,
  };
}

/**
 * @param {object|null|undefined} item
 * @returns {string}
 */
function enrichmentItemKey(item, index) {
  if (item?.sourceItemId) {
    return String(item.sourceItemId);
  }
  const label =
    item?.normalizedDescription ||
    item?.description ||
    item?.displayName ||
    "";
  if (label) {
    return `label:${String(label).trim().toLowerCase()}`;
  }
  return `index:${index}`;
}

/**
 * @param {object|null|undefined} item
 * @returns {string}
 */
function enrichmentItemLabel(item) {
  return (
    item?.normalizedDescription ||
    item?.description ||
    item?.displayName ||
    "Unnamed item"
  );
}

/**
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatEnrichmentM3(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(2)} m³`;
}

/**
 * Build side-by-side rows for catalogue vs secondary enrichment (AI or n8n).
 *
 * @param {object|null|undefined} catalogueReport
 * @param {object|null|undefined} secondaryReport
 * @param {"ai"|"n8n"|null} [secondarySource]
 * @returns {object[]}
 */
export function buildEnrichmentComparisonRows(
  catalogueReport,
  secondaryReport,
  secondarySource = null
) {
  const catalogueItems = Array.isArray(catalogueReport?.items)
    ? catalogueReport.items
    : [];
  const secondaryItems = Array.isArray(secondaryReport?.items)
    ? secondaryReport.items
    : [];
  const secondaryKey =
    secondarySource === "ai" || secondarySource === "n8n"
      ? secondarySource
      : "secondary";

  const catalogueByKey = new Map(
    catalogueItems.map((item, index) => [enrichmentItemKey(item, index), item])
  );
  const secondaryByKey = new Map(
    secondaryItems.map((item, index) => [enrichmentItemKey(item, index), item])
  );

  const keys = [
    ...new Set([...catalogueByKey.keys(), ...secondaryByKey.keys()]),
  ];

  return keys.map((key, index) => {
    const catalogueItem = catalogueByKey.get(key) || null;
    const secondaryItem = secondaryByKey.get(key) || null;
    const label =
      enrichmentItemLabel(catalogueItem) ||
      enrichmentItemLabel(secondaryItem) ||
      `Item ${index + 1}`;
    const catalogueM3 = catalogueItem?.totalM3 ?? catalogueItem?.m3 ?? null;
    const secondaryM3 = secondaryItem?.totalM3 ?? secondaryItem?.m3 ?? null;
    const delta =
      catalogueM3 != null &&
      secondaryM3 != null &&
      Number.isFinite(Number(catalogueM3)) &&
      Number.isFinite(Number(secondaryM3))
        ? Number(secondaryM3) - Number(catalogueM3)
        : null;

    return {
      key,
      label,
      quantity: catalogueItem?.quantity ?? secondaryItem?.quantity ?? null,
      catalogueM3,
      secondaryM3,
      secondaryKey,
      delta,
      catalogueConfidence: catalogueItem?.confidence ?? null,
      secondaryConfidence: secondaryItem?.confidence ?? null,
    };
  });
}

/**
 * @param {{ enrichmentReports?: object, authoritativeEstimateSource?: string, canAdoptCatalogue?: boolean, canAdoptN8n?: boolean }} review
 * @returns {string|null}
 */
export function describeAdoptButtonState(review, source) {
  const reports = review?.enrichmentReports || {};
  const authority = review?.authoritativeEstimateSource;
  const hasReport = Boolean(reports?.[source]);

  if (!hasReport) {
    if (source === "n8n") {
      return "n8n report not received yet — it will appear here when enrichment completes.";
    }
    if (source === "ai") {
      return "OS AI enrichment not received yet — it will appear here when enrichment completes.";
    }
    return "Offline catalogue report is not available on this quote.";
  }

  if (authority === source) {
    return `${formatEnrichmentSourceLabel(source)} is already the active report used for pricing.`;
  }

  if (source === "catalogue" && !review?.canAdoptCatalogue) {
    return "Catalogue report cannot be adopted right now.";
  }
  if (source === "n8n" && !review?.canAdoptN8n) {
    return "n8n report cannot be adopted right now.";
  }
  if (source === "ai" && !review?.canAdoptAi) {
    return "OS AI report cannot be adopted right now.";
  }

  return `Switch volume estimate to the ${formatEnrichmentSourceLabel(source)} report, then Adopt price when ready.`;
}
